import { analytics } from '@src/utility/analytics';
import { TRANSCRIPT_SHOW_CLIP_EVENT } from '@src/utility/transcriptEvents';
import {
  applyClipHighlights,
  clearClipHighlights,
  CLIP_MATCH_ACTIVE_CLASS,
  findClipRanges,
  type HighlightRange,
  rangesToIndices,
} from '@src/utility/transcriptQuoteUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { TranscriptShowClipDetail } from '@src/utility/transcriptEvents';
import type { RefObject } from 'react';

/**
 * The transcript's Clips marks: the header pill's on/off state, which clip is
 * active, the underline itself, and the optimistic ranges a save in progress
 * adds. Extracted from `Transcript.tsx` for its line budget (PLAT-12103), which
 * also gave the marks a way to be switched on from outside the transcript —
 * a save made elsewhere on the surface (the Co-Producer) asks, by
 * `TRANSCRIPT_SHOW_CLIP_EVENT`, for the clip it just made to be shown.
 */
export function useClipMarks({
  videoCreator,
  enabled,
  blocked,
  scrollRef,
  getScrollContainer,
  getInitialVisibleIndex,
}: {
  videoCreator: Pick<ITranscriptVideoCreator, 'story'>;
  /** The host shows the Clips pill at all (`showClipsToggle`). */
  enabled: boolean;
  /** A "Show Only" filter is active, which turns every mark off. */
  blocked: boolean;
  scrollRef: RefObject<HTMLDivElement>;
  getScrollContainer: () => HTMLElement | undefined;
  getInitialVisibleIndex: (ranges: HighlightRange[]) => number;
}) {
  const [showClips, setShowClips] = useState(false);
  // The active clip is held by its transcript range, not its place in the
  // list: a save inserts a clip mid-list, and a held index would then point at
  // a neighbour and scroll there first (PLAT-12103). The range also survives
  // an optimistic clip's temp id being swapped for its real one.
  const [activeClipKey, setActiveClipKey] = useState<string | null>(null);
  const [pendingClipRanges, setPendingClipRanges] = useState<HighlightRange[]>(
    [],
  );

  // Ranges are always computed so findFirstVisibleRangeIndex works on toggle
  const otherVideos = videoCreator.story?.otherVideos;
  const realClipRanges = useMemo(
    () => findClipRanges(otherVideos || []),
    [otherVideos],
  );
  const clipRanges = useMemo(() => {
    if (pendingClipRanges.length === 0) return realClipRanges;
    const realKeys = new Set(realClipRanges.map(r => `${r.start}-${r.end}`));
    const pending = pendingClipRanges.filter(
      r => !realKeys.has(`${r.start}-${r.end}`),
    );
    return [...realClipRanges, ...pending].sort((a, b) => a.start - b.start);
  }, [realClipRanges, pendingClipRanges]);

  const foundIndex = clipRanges.findIndex(r => rangeKey(r) === activeClipKey);
  const activeClipIndex = Math.max(foundIndex, 0);
  // A clip that is active but not in the list yet (its save has not reached
  // the story) marks nothing active, so nothing is scrolled to until it lands.
  const activeRange =
    activeClipKey === null
      ? (clipRanges[0] ?? null)
      : (clipRanges[foundIndex] ?? null);

  // Only navigating — the pill coming on, its arrows — scrolls to the active
  // clip. A save marks the clip where the editor already is, and the marks
  // re-apply whenever the clip list changes (a temp id swapped for its real
  // one); neither may move the transcript (PLAT-12103).
  const scrollOnApplyRef = useRef(false);
  const setActiveClipIndex = useCallback(
    (index: number) => {
      const range = clipRanges[index];
      const key = range ? rangeKey(range) : null;
      // No re-render would consume the flag, so it would scroll on a later save.
      if (key === activeClipKey) return;
      scrollOnApplyRef.current = true;
      setActiveClipKey(key);
    },
    [clipRanges, activeClipKey],
  );
  const setActiveClip = useCallback(
    (range: Pick<HighlightRange, 'start' | 'end'>) => {
      setActiveClipKey(rangeKey(range));
    },
    [],
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    clearClipHighlights(container);
    if (enabled && showClips && clipRanges.length > 0) {
      const allIndices = rangesToIndices(clipRanges);
      if (scrollOnApplyRef.current && activeRange) {
        scrollOnApplyRef.current = false;
        applyClipHighlights(
          container,
          allIndices,
          activeRange,
          getScrollContainer(),
        );
      } else {
        // The applier scrolls whenever it is handed an active range, so the
        // active mark is added here instead.
        applyClipHighlights(container, allIndices, null);
        for (
          let i = activeRange?.start ?? 0;
          i <= (activeRange?.end ?? -1);
          i++
        ) {
          container
            .querySelector(`[data-index='${i}']`)
            ?.classList.add(CLIP_MATCH_ACTIVE_CLASS);
        }
      }
    }
    return () => {
      clearClipHighlights(container);
    };
  }, [
    enabled,
    showClips,
    clipRanges,
    activeRange,
    getScrollContainer,
    scrollRef,
  ]);

  // Scoped like `useTranscriptIndexJump`: only the transcript bound to the
  // dispatching surface's creator answers. Show Only keeps every mark off, so
  // a request made under it is dropped rather than parked.
  useEffect(() => {
    if (!enabled || blocked) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<TranscriptShowClipDetail>).detail;
      if (!detail) return;
      if (detail.creator && detail.creator !== videoCreator) return;
      setActiveClip({ start: detail.startIndex, end: detail.endIndex });
      setShowClips(true);
    };
    window.addEventListener(TRANSCRIPT_SHOW_CLIP_EVENT, handler);
    return () => {
      window.removeEventListener(TRANSCRIPT_SHOW_CLIP_EVENT, handler);
    };
  }, [enabled, blocked, videoCreator, setActiveClip]);

  // Clips saved over the same passage draw one mark, so the arrows step past
  // them to the next passage.
  const stepClip = useCallback(
    (direction: 1 | -1) => {
      const count = clipRanges.length;
      if (count === 0) return;
      let index = activeClipIndex;
      for (let step = 0; step < count; step++) {
        index = (index + direction + count) % count;
        if (rangeKey(clipRanges[index]) !== activeClipKey) break;
      }
      setActiveClipIndex(index);
    },
    [clipRanges, activeClipIndex, activeClipKey, setActiveClipIndex],
  );
  const goNextClip = useCallback(() => stepClip(1), [stepClip]);
  const goPrevClip = useCallback(() => stepClip(-1), [stepClip]);

  // Shared by the pill's label and its dimmed nav arrows (PLAT-11039).
  const handleToggleClips = useCallback(() => {
    analytics.track('transcript_toggle_clips', {
      storyId: videoCreator.story?.id,
      storyTitle: videoCreator.story?.title,
    });
    if (!showClips) {
      setActiveClipIndex(getInitialVisibleIndex(clipRanges));
    }
    setShowClips(!showClips);
  }, [
    showClips,
    clipRanges,
    getInitialVisibleIndex,
    setActiveClipIndex,
    videoCreator.story,
  ]);

  return {
    showClips,
    setShowClips,
    activeClipIndex,
    setActiveClip,
    setPendingClipRanges,
    realClipRanges,
    clipRanges,
    goNextClip,
    goPrevClip,
    handleToggleClips,
  };
}

function rangeKey(range: Pick<HighlightRange, 'start' | 'end'>): string {
  return `${range.start}-${range.end}`;
}
